import { Users, Cake, Clapperboard, Video, Sparkles } from "lucide-react";
import { FadeUp, SectionHeader } from "./Reveal.jsx";
import { useSiteContent, mediaUrl } from "@/content/ContentContext.jsx";

const ICONS = { Users, Cake, Clapperboard, Video, Sparkles };

const CARD_VARIANTS = [
  "sm:col-span-2 lg:col-span-4 image",
  "lg:col-span-2 solid",
  "lg:col-span-2 tint",
  "lg:col-span-2 tint",
  "lg:col-span-2 image",
];

const About = () => {
  const { content } = useSiteContent();
  const c = content.about;

  return (
    <section id="about" className="relative py-20 sm:py-28 lg:py-36" data-testid="about-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-10">
        <SectionHeader eyebrow={c.eyebrow} title={c.title} testid="about-eyebrow" />

        <div className="mt-10 grid gap-10 lg:grid-cols-12">
          <FadeUp className="lg:col-span-5" delay={0.1}>
            <p className="text-lg leading-relaxed text-zinc-700" data-testid="about-lead">
              {c.lead}
            </p>
            <p className="mt-6 leading-relaxed text-zinc-600" data-testid="about-body">
              {c.body}
            </p>
          </FadeUp>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:col-span-7 lg:grid-cols-6">
            {c.cards.map((card, i) => {
              const variant = CARD_VARIANTS[i] || "lg:col-span-2 tint";
              const Icon = ICONS[card.icon] || Sparkles;
              const wrap = variant.split(" ")[0];
              const style = variant.includes("image") ? "image" : variant.includes("solid") ? "solid" : "tint";

              return (
                <FadeUp key={card.title + i} className={wrap} delay={0.05 + 0.05 * (i % 4)}>
                  {style === "image" ? (
                    <article className="group relative h-full min-h-[260px] overflow-hidden rounded-3xl" data-testid={`about-card-${i + 1}`}>
                      <img
                        src={mediaUrl(card.image)}
                        alt={card.title}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/85 via-brand-dark/25 to-transparent" />
                      <div className="relative flex h-full flex-col justify-end p-7">
                        {card.tag && (
                          <span className="w-fit rounded-full bg-white/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white backdrop-blur">
                            {card.tag}
                          </span>
                        )}
                        <h3 className="mt-3 font-display text-2xl text-white">{card.title}</h3>
                        <p className="mt-2 max-w-md text-sm leading-relaxed text-white/85">{card.desc}</p>
                      </div>
                    </article>
                  ) : style === "solid" ? (
                    <article className="flex h-full min-h-[260px] flex-col justify-between rounded-3xl bg-brand p-7 text-white" data-testid={`about-card-${i + 1}`}>
                      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/12">
                        <Icon size={22} />
                      </span>
                      <div>
                        {card.tag && <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/60">{card.tag}</span>}
                        <h3 className="mt-2 font-display text-xl leading-snug">{card.title}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-white/75">{card.desc}</p>
                      </div>
                    </article>
                  ) : (
                    <article className="flex h-full min-h-[260px] flex-col justify-between rounded-3xl bg-brand-tint2 p-7" data-testid={`about-card-${i + 1}`}>
                      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-brand">
                        <Icon size={22} />
                      </span>
                      <div>
                        {card.tag && <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-light">{card.tag}</span>}
                        <h3 className="mt-2 font-display text-xl leading-snug text-brand">{card.title}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-zinc-600">{card.desc}</p>
                      </div>
                    </article>
                  )}
                </FadeUp>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
